"use client";

import { Field, Label } from "@headlessui/react";
import { useState, useEffect, FC, FocusEvent } from "react";
import cn from "classnames";
import { ExclamationMark } from "@/icons";

type ExpectedFieldProps<T> = {
  value?: unknown;
  placeholder?: unknown;
  disabled?: boolean;
  required?: boolean;
  name?: unknown;
  onBlur?: (event: FocusEvent<T>) => void | Promise<void>;
  onFocus?: (event: FocusEvent<T>) => void | Promise<void>;
};

export type StandardFormFieldProps<T> = {
  label?: string;
  invalidText?: string;
  withFixedLabel?: boolean;
  formControl: FC<
    Omit<T, "label" | "invalidText" | "withFixedLabel" | "formControl">
  >;
} & T;
export const StandardFormField = function <K, T extends ExpectedFieldProps<K>>({
  label,
  invalidText,
  withFixedLabel = false,
  formControl: FormControl,
  ...props
}: StandardFormFieldProps<T>) {
  const [moveLabelUp, setMoveLabelUp] = useState<boolean>(
    Boolean(withFixedLabel),
  );

  const onFocus = async (event: FocusEvent<K>) => {
    setMoveLabelUp(
      withFixedLabel ||
        Boolean(props.value) ||
        Boolean(props.placeholder) ||
        true,
    );
    await props?.onFocus?.(event);
  };

  const onBlur = async (event: FocusEvent<K>) => {
    setMoveLabelUp(
      withFixedLabel ||
        Boolean(props.value) ||
        Boolean(props.placeholder) ||
        false,
    );
    await props?.onBlur?.(event);
  };

  useEffect(() => {
    if (props.value || props.placeholder || withFixedLabel) {
      setMoveLabelUp(true);
    } else if (!withFixedLabel && (!props.value || !props.placeholder)) {
      setMoveLabelUp(false);
    }
  }, [props.value, props.placeholder, withFixedLabel]);

  return (
    <Field
      className="group relative flex w-full flex-col py-4"
      disabled={props.disabled}
    >
      {label && (
        <Label
          className={cn(
            "text-swdc-gray-300 absolute cursor-text",
            moveLabelUp && "text-body-2xs top-0 left-0 font-medium",
            props.required && "after:text-swdc-red-500 after:content-['*'/'']",
          )}
        >
          {label}
        </Label>
      )}
      <div
        className={cn(
          "text-body-m text-swdc-black-600 placeholder:text-swdc-gray-300 mb-0.5 flex min-h-6 flex-row items-center gap-x-1 border-b-2 transition-colors duration-300 ease-linear outline-none sm:text-base",
          "invalid" in props && props.invalid
            ? "border-swdc-red-500"
            : "border-swdc-silver-300 group-focus-within:border-swdc-black-600 group-focus:border-swdc-black-600 group-active:border-swdc-black-600",
        )}
      >
        <FormControl
          aria-errormessage={`invalid-${props.name}`}
          {...props}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
      {"invalid" in props && Boolean(props.invalid) && (
        <div className="flex items-center gap-x-2 pt-px">
          <ExclamationMark className="flex-none" aria-hidden />
          <span
            className="text-body-2xs text-swdc-red-500 text-left leading-none font-medium"
            id={`invalid-${props.name}`}
          >
            {invalidText}
          </span>
        </div>
      )}
    </Field>
  );
};
